'use client';

import React from 'react';
import { ChevronRight, GitBranch } from 'lucide-react';
import type { PostCardProps } from './post-card';
import { stripMarkdown } from '@/lib/community/utils';
import { SmartCoverImage } from './smart-cover-image';
import { PostCardStats } from './post-card-stats';

export const PostCardWorkflow = React.memo(function PostCardWorkflow({ post, onClick, onTagClick, onLike, onFavorite }: PostCardProps) {
	const stepCount = post.stepCount ?? 0;

	return (
		<div
			className="liquid-glass-card rounded-md overflow-hidden cursor-pointer"
			onClick={(e) => {
				const card = e.currentTarget;
				const cover = card.querySelector('[data-cover]');
				onClick?.(post, card.getBoundingClientRect(), cover?.getBoundingClientRect() ?? undefined);
			}}
		>
			{post.coverImage && (
				<div data-cover>
					<SmartCoverImage alt={post.title} src={post.coverImage} />
				</div>
			)}

			<div className="p-3 flex flex-col gap-2">
				<h3 className="font-medium text-sm leading-snug truncate">{post.title}</h3>

				<p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{stripMarkdown(post.description)}</p>

				{post.workflowNodes && post.workflowNodes.length > 0 ? (
					<div className="flex items-start gap-1.5 rounded-md border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 w-fit">
						<GitBranch className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-1 flex-wrap">
								{post.workflowNodes.slice(0, 4).map((node, i) => (
									<span className="inline-flex items-center gap-1" key={node.id}>
										{i > 0 && <ChevronRight className="w-3 h-3 text-emerald-600/50 dark:text-emerald-500/50 shrink-0" />}
										<span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-500/12 text-emerald-700 dark:text-emerald-400/80 truncate max-w-[72px]">
											{node.name}
										</span>
									</span>
								))}
								{post.workflowNodes.length > 4 && <span className="text-[10px] text-emerald-600/60 dark:text-emerald-500/60">+{post.workflowNodes.length - 4}</span>}
							</div>
							<p className="text-[9px] text-muted-foreground mt-1">
								{post.workflowNodes.length} 个节点
								{post.workflowNodes.some((n) => n.dependencies.length > 0) && ' · 含依赖关系'}
							</p>
						</div>
					</div>
				) : stepCount > 0 ? (
					<span className="text-[11px] font-medium text-muted-foreground">{stepCount} 步工作流</span>
				) : null}

				{post.tags.length > 0 && (
					<div className="flex flex-wrap gap-1">
						{post.tags.slice(0, 3).map((tag) => (
							<button
								className="inline-block rounded-full bg-secondary/70 text-foreground/70 px-2 py-0.5 text-[10px] hover:text-foreground hover:bg-secondary transition-colors cursor-pointer"
								key={tag}
								onClick={(e) => {
									e.stopPropagation();
									onTagClick?.(tag);
								}}
								type="button"
							>
								#{tag}
							</button>
						))}
					</div>
				)}

				<PostCardStats onFavorite={onFavorite} onLike={onLike} post={post} />
			</div>
		</div>
	);
});
